import axios, { AxiosHeaders, type AxiosError, type InternalAxiosRequestConfig } from 'axios'

export const AUTH_TOKEN_STORAGE_KEY = 'shopora_admin_token'
export const AUTH_REFRESH_TOKEN_STORAGE_KEY = 'shopora_admin_refresh_token'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
}

export function persistTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, accessToken)
  localStorage.setItem(AUTH_REFRESH_TOKEN_STORAGE_KEY, refreshToken)
}

/**
 * Login / change-password failures — do not treat as session expiry and do not refresh.
 */
function isCredentialAuthError(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false
  const code = (data as { error?: { code?: string } | null }).error?.code
  return code === 'INVALID_CREDENTIALS' || code === 'INVALID_CURRENT_PASSWORD'
}

/**
 * True when the session should end (expired/invalid/missing auth), not for wrong login password.
 */
export function isSessionExpiredApiError(status: number, data: unknown): boolean {
  if (status !== 401) return false
  if (isCredentialAuthError(data)) return false
  return true
}

/** Attempt refresh for any auth failure except credential errors and an already-invalid refresh token. */
function shouldAttemptRefresh(status: number, data: unknown): boolean {
  if (status !== 401) return false
  if (isCredentialAuthError(data)) return false
  if (!data || typeof data !== 'object') return true
  const code = (data as { error?: { code?: string } | null }).error?.code
  if (code === 'INVALID_REFRESH_TOKEN') return false
  return true
}

let sessionInvalidHandler: (() => void) | null = null

/** Registered by `AuthProvider` when the access token can no longer be refreshed. */
export function setSessionInvalidHandler(handler: (() => void) | null) {
  sessionInvalidHandler = handler
}

function notifySessionInvalid(err: { response?: { status?: number; data?: unknown } }) {
  const status = err.response?.status
  const data = err.response?.data
  if (status !== undefined && isSessionExpiredApiError(status, data)) {
    sessionInvalidHandler?.()
  }
}

let refreshInFlight: Promise<void> | null = null

interface RefreshEnvelope {
  success: boolean
  data?: { accessToken: string; refreshToken: string }
}

async function performTokenRefresh(): Promise<void> {
  const rt = getStoredRefreshToken()
  if (!rt) throw new Error('No refresh token')
  const { data } = await api.post<RefreshEnvelope>(
    '/auth/refresh',
    { refreshToken: rt },
    { skipAuthRefresh: true },
  )
  const payload = data?.data
  if (!data?.success || !payload?.accessToken || !payload?.refreshToken) {
    throw new Error('Invalid refresh response')
  }
  persistTokens(payload.accessToken, payload.refreshToken)
}

function refreshTokensSingleFlight(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = performTokenRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

api.interceptors.request.use((config) => {
  const token = getStoredAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const cfg = error.config as InternalAxiosRequestConfig | undefined
    if (!error.response || !cfg) {
      return Promise.reject(error)
    }

    if (cfg.skipAuthRefresh) {
      if (cfg.url?.includes('/auth/refresh') && error.response.status === 401) {
        sessionInvalidHandler?.()
      }
      return Promise.reject(error)
    }

    const status = error.response.status
    const data = error.response.data

    if (
      status === 401 &&
      shouldAttemptRefresh(status, data) &&
      getStoredRefreshToken() &&
      !cfg._retry
    ) {
      cfg._retry = true
      try {
        await refreshTokensSingleFlight()
        const token = getStoredAccessToken()
        if (!token) throw new Error('Missing access token after refresh')
        const next = AxiosHeaders.from(cfg.headers ?? {})
        next.set('Authorization', `Bearer ${token}`)
        cfg.headers = next
        return api.request(cfg)
      } catch {
        sessionInvalidHandler?.()
        return Promise.reject(error)
      }
    }

    notifySessionInvalid(error)
    return Promise.reject(error)
  },
)
